import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  VStack,
  HStack,
  Text,
  Textarea,
  Icon,
  Box,
  Badge,
  Alert,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { toaster } from '@/components/ui/toaster';
import { FaStar, FaRegStar, FaMusic, FaClock, FaComments, FaThumbsUp, FaHandshake, FaUserTie } from 'react-icons/fa';
import { resenasAPI } from '../services/api';

function getInitialRatings(userType) {
  return userType === 'banda'
    ? { comunicacion: null, formalidad: null, atencion: null, general: null }
    : { puntualidad: null, comunicacion: null, calidadArtistica: null, general: null };
}

const RatingPicker = ({ label, value, onChange, icon, description }) => {
  const getRatingLabel = (val) => {
    switch (val) {
      case 1:
        return 'Muy malo';
      case 2:
        return 'Malo';
      case 3:
        return 'Normal';
      case 4:
        return 'Bueno';
      case 5:
        return 'Excelente';
      default:
        return '';
    }
  };

  const getRatingColor = (val) => {
    if (val == null) return 'gray';
    if (val <= 2) return 'red';
    if (val === 3) return 'yellow';
    return 'green';
  };

  return (
    <Field.Root>
      <HStack justify="space-between" mb={2}>
        <HStack>
          <Icon as={icon} color="purple.500" />
          <Field.Label mb={0} fontWeight="600">
            {label}
          </Field.Label>
        </HStack>
        <Badge colorPalette={getRatingColor(value)} px={3} py={1} rounded="full">
          {value == null ? 'Sin valorar' : `${value}/5 · ${getRatingLabel(value)}`}
        </Badge>
      </HStack>
      <HStack gap={2} flexWrap="wrap">
        {[1, 2, 3, 4, 5].map((n) => (
          <Button
            key={n}
            size="sm"
            minW="44px"
            variant={value === n ? 'solid' : 'outline'}
            colorPalette={value === n ? 'purple' : 'gray'}
            onClick={() => onChange(n)}
          >
            {n}
          </Button>
        ))}
      </HStack>
      {description && (
        <Field.HelperText fontSize="xs">{description}</Field.HelperText>
      )}
    </Field.Root>
  );
};

const StarDisplay = ({ rating }) => (
  <HStack gap={1}>
    {[1, 2, 3, 4, 5].map((star) => (
      <Icon
        key={star}
        as={star <= rating ? FaStar : FaRegStar}
        color={star <= rating ? 'yellow.400' : 'gray.300'}
        w={6}
        h={6}
      />
    ))}
  </HStack>
);

const ReviewForm = ({ isOpen, onClose, evento, onReviewSubmitted, userType = 'cofradia' }) => {
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emptyRatings = useMemo(() => getInitialRatings(userType), [userType]);
  const [ratings, setRatings] = useState(emptyRatings);
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (isOpen) {
      setRatings(getInitialRatings(userType));
      setComment('');
    }
  }, [isOpen, userType]);

  const ratingKeys =
    userType === 'banda'
      ? ['comunicacion', 'formalidad', 'atencion', 'general']
      : ['puntualidad', 'comunicacion', 'calidadArtistica', 'general'];

  const handleSubmit = async () => {
    if (!evento?.id || (userType === 'cofradia' && !evento?.bandaId) || (userType === 'banda' && !evento?.cofradiaId)) {
      toaster.create({
        title: 'Error',
        description: 'Información del evento incompleta',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    const falta = ratingKeys.filter((k) => ratings[k] == null);
    if (falta.length > 0) {
      toaster.create({
        title: 'Valoraciones incompletas',
        description: 'Selecciona una puntuación del 1 al 5 en cada criterio antes de enviar.',
        type: 'warning',
        duration: 4000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const reviewData = {
        eventoId: evento.id,
        autorUserType: userType === 'banda' ? 'BANDA' : 'COFRADIA',
        ratingComunicacion: ratings.comunicacion,
        ratingGeneral: ratings.general,
        comment: comment.trim() || undefined,
      };

      if (userType === 'banda') {
        reviewData.ratingFormalidad = ratings.formalidad;
        reviewData.ratingAtencion = ratings.atencion;
      } else {
        reviewData.ratingPuntualidad = ratings.puntualidad;
        reviewData.ratingCalidadArtistica = ratings.calidadArtistica;
      }

      await resenasAPI.create(reviewData);

      const targetName = userType === 'banda' ? 'cofradía' : 'banda';
      toaster.create({
        title: 'Reseña enviada',
        description: `Tu reseña ha sido enviada correctamente. La ${targetName} podrá decidir si hacerla pública.`,
        type: 'success',
        duration: 5000,
      });

      setRatings(getInitialRatings(userType));
      setComment('');

      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'Error al enviar la reseña',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const vals =
    userType === 'banda'
      ? [ratings.comunicacion, ratings.formalidad, ratings.atencion, ratings.general]
      : [ratings.puntualidad, ratings.comunicacion, ratings.calidadArtistica, ratings.general];

  const todasDefinidas = vals.every((v) => v != null);
  const averageRating = todasDefinidas ? Math.round(vals.reduce((a, b) => a + b, 0) / 4) : null;

  const targetName = userType === 'banda' ? 'cofradía' : 'banda';
  const targetAvatar =
    userType === 'banda'
      ? evento?.cofradia?.avatar || evento?.cofradiaAvatar
      : evento?.banda?.avatar || evento?.bandaAvatar;
  const targetNombre =
    userType === 'banda'
      ? evento?.cofradia?.nombre || evento?.cofradiaName
      : evento?.banda?.nombre || evento?.bandaName;

  return (
    <Dialog.Root open={isOpen} size='xl' placement='center' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(5px)" />
        <Dialog.Positioner>
          <Dialog.Content rounded="2xl" mx={4}>
            <Dialog.Header pb={0}>
              <VStack align="start" gap={1}>
                <Text fontSize="2xl" fontWeight="bold">
                  Dejar reseña
                </Text>
                <Text fontSize="sm" color="gray.500" fontWeight="normal">
                  Tu opinión ayuda a mejorar la plataforma
                </Text>
              </VStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />

            <Dialog.Body py={6}>
              <VStack gap={6} align="stretch">
                {evento && (
                  <Box bg="purple.50" p={4} rounded="xl">
                    <HStack gap={4}>
                      <Avatar.Root size="lg"><Avatar.Fallback name={targetNombre} /><Avatar.Image src={targetAvatar} /></Avatar.Root>
                      <VStack align="start" gap={0} flex={1}>
                        <Text fontWeight="bold" fontSize="lg">
                          {targetNombre}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Evento: {evento.eventName || evento.nombre}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {new Date(evento.date || evento.fecha).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                )}

                <Alert.Root status="info" rounded="lg">
                  <Alert.Indicator />
                  <Box>
                    <Text fontSize="sm" mb={1}>
                      La {targetName} recibirá esta reseña y podrá decidir si hacerla pública en su perfil.
                    </Text>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">
                      Elige del 1 al 5 en cada criterio cuando quieras valorar (1 = muy malo, 5 = excelente).
                    </Text>
                  </Box>
                </Alert.Root>

                <VStack gap={5} align="stretch">
                  {userType === 'banda' ? (
                    <>
                      <RatingPicker
                        label="Comunicación"
                        value={ratings.comunicacion}
                        onChange={(val) => setRatings((prev) => ({ ...prev, comunicacion: val }))}
                        icon={FaComments}
                        description="¿Fue fácil contactar y coordinar con la cofradía?"
                      />

                      <RatingPicker
                        label="Formalidad"
                        value={ratings.formalidad}
                        onChange={(val) => setRatings((prev) => ({ ...prev, formalidad: val }))}
                        icon={FaHandshake}
                        description="¿Cumplieron con los términos del acuerdo y compromisos?"
                      />

                      <RatingPicker
                        label="Atención"
                        value={ratings.atencion}
                        onChange={(val) => setRatings((prev) => ({ ...prev, atencion: val }))}
                        icon={FaUserTie}
                        description="¿Fue atenta y considerada la cofradía durante el evento?"
                      />

                      <RatingPicker
                        label="Valoración general"
                        value={ratings.general}
                        onChange={(val) => setRatings((prev) => ({ ...prev, general: val }))}
                        icon={FaThumbsUp}
                        description="Tu impresión general de la experiencia"
                      />
                    </>
                  ) : (
                    <>
                      <RatingPicker
                        label="Puntualidad"
                        value={ratings.puntualidad}
                        onChange={(val) => setRatings((prev) => ({ ...prev, puntualidad: val }))}
                        icon={FaClock}
                        description="¿Llegaron a tiempo y cumplieron los horarios acordados?"
                      />

                      <RatingPicker
                        label="Comunicación"
                        value={ratings.comunicacion}
                        onChange={(val) => setRatings((prev) => ({ ...prev, comunicacion: val }))}
                        icon={FaComments}
                        description="¿Fue fácil contactar y coordinar con ellos?"
                      />

                      <RatingPicker
                        label="Calidad artística"
                        value={ratings.calidadArtistica}
                        onChange={(val) => setRatings((prev) => ({ ...prev, calidadArtistica: val }))}
                        icon={FaMusic}
                        description="¿Cómo valoras la interpretación musical y profesionalidad?"
                      />

                      <RatingPicker
                        label="Valoración general"
                        value={ratings.general}
                        onChange={(val) => setRatings((prev) => ({ ...prev, general: val }))}
                        icon={FaThumbsUp}
                        description="Tu impresión general de la experiencia"
                      />
                    </>
                  )}
                </VStack>

                <Box bg="gray.50" p={4} rounded="xl" textAlign="center">
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    Valoración media
                  </Text>
                  <HStack justify="center" gap={2}>
                    {averageRating != null ? (
                      <>
                        <StarDisplay rating={averageRating} />
                        <Text fontSize="2xl" fontWeight="bold">
                          {averageRating}/5
                        </Text>
                      </>
                    ) : (
                      <Text fontSize="md" color="gray.500">
                        Se calculará al completar los cuatro criterios
                      </Text>
                    )}
                  </HStack>
                </Box>

                <Field.Root>
                  <Field.Label fontWeight="600">Comentario (opcional)</Field.Label>
                  <Textarea
                    placeholder={`Comparte tu experiencia con ${userType === 'banda' ? 'esta cofradía' : 'esta banda'}...`}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={4}
                    resize="none"
                  />
                  <Field.HelperText>
                    Puedes añadir detalles sobre tu experiencia. Sé respetuoso y constructivo.
                  </Field.HelperText>
                </Field.Root>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer borderTop="1px" borderColor="gray.100" pt={4}>
              <HStack gap={3} w="full" justify="flex-end">
                <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  loadingText="Enviando..."><Icon as={FaStar} />Enviar reseña
                              </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
};

export default ReviewForm;
